if (!customElements.get('product-form')) {
  // How long a tap on Add waits for <cart-drawer> to be defined before the form posts the old way.
  const CART_READY_TIMEOUT = 3000;
  const REQUEST_TIMEOUT = 15000;
  // The repaint's recovery request: the add is already confirmed, so the button should not wait long for it.
  const RECOVERY_TIMEOUT = 5000;
  // How long an Add waits for a cart request that is already pending (window.pmCartLock, assets/cart.js: a quantity
  // change, a "Pairs well" Add) to finish. Longer than the most a holder keeps the lock: up to 0.5 s of debounce or
  // prefetch, a 15 s request, then a 5 s resync after a failure. Proceeding sooner would let that resync redraw the
  // older cart over this Add's line.
  const CART_LOCK_WAIT = 22000;

  // fetch() that also reads the body as JSON and aborts after `timeout`, so a stalled connection cannot leave
  // the button spinning. Rejects on a timeout, a network error and an answer that is not a JSON object.
  const fetchJson = async (url, options = {}, timeout = REQUEST_TIMEOUT) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const data = await response.json();
      if (!data || typeof data !== 'object') throw new Error(`${response.url} did not answer with a JSON object`);
      return { response, data };
    } finally {
      clearTimeout(timer);
    }
  };

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.nativeSubmit) return;

        this.handleErrorMessage();
        this.removeAddedNote();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Nothing before the request may leave the button spinning: whatever goes wrong here, the form posts natively.
        let config;
        let formData;
        const lock = window.pmCartLock;
        let ownsLock = false;
        try {
          // Looked up again at submit time. <cart-drawer> is an empty HTMLElement until cart-drawer.js,
          // the last deferred script on the page, has run, and a tap can land before that.
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (this.cart && !this.cartIsReady() && !(await this.whenCartReady())) {
            this.submitNatively();
            return;
          }

          // One cart request at a time (window.pmCartLock, assets/cart.js): the Add starts once any other has
          // answered and holds the lock while it is in flight. A lock still held after CART_LOCK_WAIT is somebody
          // else's, so it is neither taken nor released.
          const deadline = Date.now() + CART_LOCK_WAIT;
          while (lock && lock.held && Date.now() < deadline) await new Promise((resolve) => setTimeout(resolve, 50));
          if (lock && !lock.held) {
            ownsLock = true;
            lock.lock();
          }

          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        } catch (e) {
          console.error(e);
          if (ownsLock) lock.unlock();
          this.resetButton();
          this.submitNatively();
          return;
        }

        fetchJson(`${routes.cart_add_url}`, config)
          .catch((e) => {
            // a timeout, a network error or an answer that is not JSON: the item may or may not be in the cart
            console.error(e);
            this.showUnconfirmedNote();
          })
          .then((answer) => {
            if (!answer) return;
            const response = answer.data;

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              // Shopify's own sentence (a 422) if there is one, else the body's message; with neither,
              // nothing to show but the "couldn't confirm" note
              const message = typeof response.message === 'string' ? response.message.trim() : '';
              const failure = response.description || message;
              if (failure) this.handleErrorMessage(failure);
              else this.showUnconfirmedNote();

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            // a subscriber that throws must not stop the cart from being painted
            try {
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
            } catch (e) {
              console.error(e);
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              return this.paintCart(response).then((painted) => {
                if (!painted) this.showAddedNote(response.product_title);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (ownsLock) lock.unlock();
            // the drawer's own renderContents keeps its is-empty state in step with the cart
            this.resetButton();

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      resetButton() {
        this.submitButton.classList.remove('loading');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      cartIsReady() {
        return typeof this.cart.getSectionsToRender === 'function' && typeof this.cart.renderContents === 'function';
      }

      whenCartReady() {
        let timer;
        const timeout = new Promise((resolve) => {
          timer = setTimeout(resolve, CART_READY_TIMEOUT);
        });
        return Promise.race([customElements.whenDefined(this.cart.localName), timeout]).then(() => {
          clearTimeout(timer);
          return this.cartIsReady();
        });
      }

      // Posts the form the way a browser would: /cart/add, then the cart page.
      submitNatively() {
        this.nativeSubmit = true;
        // the back/forward cache restores this page as it is, loading state included
        window.addEventListener('pageshow', (event) => {
          if (!event.persisted) return;
          this.nativeSubmit = false;
          this.resetButton();
        });
        HTMLFormElement.prototype.submit.call(this.form);
      }

      // True once the drawer is repainted and open. If the response cannot be painted (no `sections`,
      // or none that holds the drawer) the item is in the cart anyway, so ask for the sections again.
      async paintCart(response) {
        const paint = (state) => {
          let opening;
          CartPerformance.measure("add:paint-updated-sections", () => {
            opening = this.cart.renderContents(state);
          });
          return opening;
        };

        try {
          await paint(response);
          return true;
        } catch (e) {
          console.error(e);
          if (this.cart.localName !== 'cart-drawer') throw e;
        }

        try {
          const { response: fresh, data: sections } = await fetchJson(
            `${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`,
            {},
            RECOVERY_TIMEOUT
          );
          if (!fresh.ok) throw new Error(`${fresh.url} answered ${fresh.status}`);
          await paint({ ...response, sections });
          return true;
        } catch (e) {
          console.error(e);
          return false;
        }
      }

      // The add went through but the drawer could not show it: say so under the button, with a way to the cart.
      showAddedNote(productTitle) {
        const added = this.cart.addedText(productTitle);
        this.showNote(added ? `${added} \u00b7 ` : '', this.cart.dataset.viewCart || 'View cart');
      }

      // The add got no usable answer (no reply in time, a network error, a body that is not JSON), so the
      // item may or may not be in the cart. Never retried from here: that could add it twice.
      showUnconfirmedNote() {
        const strings = this.cart ? this.cart.dataset : {};
        this.showNote(
          `${strings.unconfirmed || "We couldn't confirm that."} `,
          strings.checkCart || 'Check your cart'
        );
      }

      showNote(lead, linkLabel) {
        this.removeAddedNote();

        const link = document.createElement('a');
        link.href = window.routes.cart_url;
        link.textContent = linkLabel;

        const note = document.createElement('p');
        note.className = 'pm-added-note';
        note.setAttribute('role', 'status');
        this.submitButton.parentElement.insertAdjacentElement('afterend', note);
        this.addedNote = note;
        // a status region is only announced when its text arrives after it is in the DOM
        setTimeout(() => {
          if (!note.isConnected) return;
          note.append(lead, link);
          this.revealIfOffscreen(note);
        }, 100);
      }

      // Feedback that appears outside the viewport (the add came from the sticky bar, so the Add button and
      // what sits under it are scrolled away) is brought to the middle of the screen.
      revealIfOffscreen(element) {
        const rect = element.getBoundingClientRect();
        if (rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth) {
          return;
        }
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        element.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      removeAddedNote() {
        if (!this.addedNote) return;
        this.addedNote.remove();
        this.addedNote = null;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          this.revealIfOffscreen(this.errorMessageWrapper);
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
